<template>
  <div>
    <CtaArea
      :button-path="PATH_INVOICE"
      :button-text="ctaButtonText"
      :has-custom-image="hasCustomImage"
      :heading="title"
      :image-url="getCtaImage()"
      heading-kind="h1"
    />
    <div class="main main--white-background">
      <div class="content-block">
        <img
          class="content-block__arrow"
          :src="require('~/images/arrow.webp')"
        >
        <div v-html="examplesParagraph" />
      </div>
    </div>

    <div class="block">
      <template v-if="backgroundImage">
        <div
          class="block__background"
          :style="`background-image: url(${backgroundImage});`"
        />
        <div
          class="block__background--right"
          :style="`background-image: url(${backgroundImage});`"
        />
      </template>

      <div class="block__content">
        <div class="invoice-wrapper p-12 px-md-40 py-md-48 new-form-enabled">
          <InvoiceItems />
          <InvoiceSummary />
          <Button
            icon="icon icon-invoice-new"
            new-design
            size="large"
            type="warning"
            @click="createInvoice"
          >
            {{ $t('pages.profession.button') }}
          </Button>
        </div>
      </div>
    </div>

    <div class="main main--white-background">
      <div
        class="content-block content-block--with-background"
        v-if="invoiceContent"
      >
        <img
          :src="require('~/images/bulb.webp')"
          class="content-block__image"
          height="80"
          width="80"
        >
        <div v-html="invoiceContent" />
      </div>
    </div>

    <div class=" main main--white-background">
      <div class="content-block content-block--spaced content-block--last-heading-spaced">
        <img
          class="content-block__arrow"
          :src="require('~/images/arrow.webp')"
        >
        <div v-html="createInvoiceCta" />
      </div>
    </div>

    <CtaArea
      :button-path="PATH_INVOICE"
      :button-text="ctaButtonText"
      :has-custom-image="hasCustomImage"
      :heading="ctaText"
      :image-url="getCtaImage()"
      button-size="mega"
      class="cta-area--last-on-page"
    />
  </div>
</template>

<script>
import { PROFESSION_QUERY } from '~/queries';
import { PATH_INVOICE } from '~/router';
import { isEmpty } from 'ramda';
import { mapGetters, mapMutations } from 'vuex';

export default {
  name: 'ProfessionPage',
  components: {
    CtaArea: () => import('~/components/CtaArea.vue'),
    InvoiceItems: () => import('~/components/Profession/InvoiceItems.vue'),
    InvoiceSummary: () => import('~/components/v2/InvoiceForm/sections/InvoiceSummary.vue'),
  },
  data: () => ({
    PATH_INVOICE,
    hasCustomImage: false,
  }),
  apollo: {
    profession: {
      query: PROFESSION_QUERY,
      variables() {
        return {
          slug: this.$route.params.slug,
          locale: document.documentElement.lang,
          preview: !!this.$route?.query?.preview,
        };
      },
      result({ data }) {
        data?.profession?.invoiceLines?.forEach((line) => {
          this.$store.dispatch('invoiceForm/addInvoiceItemWithData', line);
        });
        this.setZeroVatRates();
      },
    },
  },
  computed: {
    ...mapGetters('invoiceForm', ['linesWithoutDeleted']),
    backgroundImage() {
      if (isEmpty(this.profession?.backgroundImage)) {
        return null;
      }
      return this.profession?.backgroundImage?.large;
    },
    createInvoiceCta() {
      return this.profession?.createInvoiceCta;
    },
    ctaButtonText() {
      return this.profession?.ctaButtonText;
    },
    ctaText() {
      return this.profession?.ctaText;
    },
    examplesParagraph() {
      return this.profession?.examplesParagraph;
    },
    invoiceContent() {
      return this.profession?.invoiceContent;
    },
    title() {
      return this.profession?.title;
    },
    loading() {
      return this.$apollo.loading;
    },
  },
  methods: {
    ...mapMutations('invoiceForm', ['setZeroVatRates']),
    getCtaImage() {
      if (isEmpty(this.profession?.ctaImage)) {
        this.hasCustomImage = false;
        return '';
      }

      this.hasCustomImage = true;
      return this.profession?.ctaImage?.medium;
    },
    createInvoice() {
      this.$router.push({
        name: PATH_INVOICE,
        params: { lines: this.linesWithoutDeleted },
      });
    },
  },
  mounted() {
    this.$store.dispatch('invoiceForm/resetState');
    this.$store.dispatch('invoiceForm/updateStoreValue', {
      name: 'kind',
      value: 'invoice',
    });
    this.$store.dispatch('invoiceForm/computeVisibilities');
  },
};
</script>

<style lang="scss" scoped>
.main {
  min-height: unset;
  margin-bottom: 48px;
}

.block {
  padding: 0;
  position: relative;
}

.invoice-wrapper {
  background-color: $--color-white;
  border: solid 1px $--border-color-base;
  border-radius: 12px;
  text-align: center;
  position: relative;
}

.block__background,
.block__background--right {
  background-position: left;
  background-repeat: no-repeat;
  background-size: contain;
  clip-path: inset(0 50% 0 0);
  height: 100%;
  left: 50%;
  max-width: 2200px;
  opacity: .6;
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  width: 100%;

  @media only screen and (max-width: $--xl-custom - 1px) {
    background-image: none !important;
  }
}

.block__background--right {
  background-position: right;
  clip-path: inset(0 0 0 50%);
}

.content-block {
  max-width: rem(872px);
  margin: rem(48px) auto;
  padding: 0 rem(24px);
  position: relative;
  z-index: 2;

  *:last-child {
    margin-bottom: 0;
  }

  @media only screen and (max-width: $--sm - 1px) {
    padding: 0;
  }

  &__arrow {
    position: absolute;
    width: 198px;
    height: auto;
    left: -120px;
    bottom: -80px;

    @media only screen and (max-width: $--new-navbar-breakpoint - 1px) {
      display: none;
    }
  }

  &__image {
    @media only screen and (max-width: $--sm - 1px) {
      width: 64px;
      height: auto;
    }
  }

  :deep() {
    h3,
    h4,
    h5 {
      font-weight: 600;
      line-height: 160%;
      margin-bottom: 16px;
    }

    h2 {
      line-height: 150%;
      font-weight: 700;
      font-size: rem(32px);
    }

    p {
      font-size: rem(20px);
      line-height: 150%;
      margin-bottom: 16px;

      &:empty {
        margin-bottom: 0;
      }
    }

    strong {
      font-weight: 600;
    }

    ul {
      padding-left: rem(22px);
      margin-bottom: 24px;

      li {
        padding-left: 6px;
      }

      p {
        margin: 0;
      }
    }
  }
}

.content-block--with-background {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  background-color: $--color-background;
  padding: rem(32px) rem(40px);
  border-radius: 16px;

  :deep() {
    h3,
    h4,
    p {
      margin-bottom: 12px;
    }

    h4 {
      margin: 40px 0 24px;
    }
  }

  @media only screen and (max-width: $--sm - 1px) {
    flex-direction: column;
    padding-left: rem(32px);
    padding-right: rem(32px);
  }

  @media only screen and (max-width: $--xs - 1px) {
    padding: rem(24px);
  }
}

.content-block--spaced {
  margin-top: 96px;
  margin-bottom: 88px;

  .content-block__arrow {
    bottom: -120px;
  }

  @media only screen and (max-width: $--md - 1px) {
    margin-top: 48px;
    margin-bottom: 48px;
  }
}

.content-block--with-image {
  z-index: 1;
}

.content-block--last-heading-spaced {
  :deep() {
    h4:last-child {
      margin-top: 32px;
    }
  }
}
</style>
